import React, { useState } from 'react';
import {
  Button,
  Flex,
  FlexItem,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { CheckIcon, PlusIcon, TimesIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ProductCategory } from '../../../types/models/ProductCategory';
import type { ApiResponseProductCategory } from '../../../types/api/ApiResponseAdminProduct';

interface CategorySelectFieldProps {
  /** Unique DOM id prefix so the two product modals don't collide. */
  idPrefix: string;
  categories: ProductCategory[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  /** Selected category id (0 = none selected, pairs with `placeholder`). */
  value: number;
  onChange: (categoryId: number) => void;
  /** Reloads the categories list after an inline create so the new option appears. */
  refetchCategories: () => Promise<void>;
  isDisabled?: boolean;
  /** Field-level error from the parent (the backend's `product_category_id`). */
  error?: string;
  /** Leading disabled option (value 0) shown for the "must choose" create flow. */
  placeholder?: string;
  /** Shown as the sole option when the list can't load, so a current selection still displays (edit). */
  fallbackOption?: { id: number; name: string };
  /**
   * A category that can't be chosen for this product, listed but disabled with the reason appended
   * to its label (e.g. "Special" for an hourly product, which the backend refuses there).
   */
  disabledCategory?: { name: string; reason: string };
}

/**
 * A category `FormSelect` with an inline "+" to create a category on the fly (POST
 * /admin/products/categories). On success the list is refetched and the new category is auto-selected,
 * so the staff member never has to leave the product form to add a missing category. Shared by the
 * create and edit product modals.
 */
function CategorySelectField({
  idPrefix,
  categories,
  categoriesLoading,
  categoriesError,
  value,
  onChange,
  refetchCategories,
  isDisabled,
  error,
  placeholder,
  fallbackOption,
  disabledCategory,
}: CategorySelectFieldProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const cancelCreate = () => {
    setIsCreating(false);
    setNewName('');
    setCreateError(null);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (name === '') {
      setCreateError('Category name is required.');
      return;
    }
    setIsSaving(true);
    setCreateError(null);
    try {
      const response = await apiFetch('/admin/products/categories', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseProductCategory | null;
      if (body?.status === 'success' && body.data?.category) {
        const created = body.data.category;
        // Refresh the list so the new option exists before we select it; a refetch hiccup shouldn't
        // undo a successful create, so select optimistically either way.
        try {
          await refetchCategories();
        } catch {
          /* ignore — the create succeeded; the list will catch up on next open */
        }
        onChange(created.id);
        addNotification({
          title: 'Category created',
          srTitle: 'Success alert',
          description: `“${created.name}” has been created and selected.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setIsCreating(false);
        setNewName('');
        return;
      }
      const message = body?.data?.name ?? body?.message ?? 'Could not create the category.';
      setCreateError(message);
      addNotification({
        title: 'Could not create category',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setCreateError(message);
      addNotification({
        title: 'Could not create category',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (categoriesLoading && categories.length === 0) {
    return <Spinner size="md" aria-label="Loading categories" />;
  }

  const options: React.ReactNode[] = [];
  if (placeholder) {
    options.push(<FormSelectOption key="__placeholder" value={0} label={placeholder} isDisabled />);
  }
  if (categories.length > 0) {
    categories.forEach((category) => {
      const isBlocked = disabledCategory !== undefined && category.name === disabledCategory.name;
      options.push(
        <FormSelectOption
          key={category.id}
          value={category.id}
          label={isBlocked ? `${category.name} (${disabledCategory.reason})` : category.name}
          isDisabled={isBlocked}
        />,
      );
    });
  } else if (fallbackOption) {
    options.push(
      <FormSelectOption key={fallbackOption.id} value={fallbackOption.id} label={fallbackOption.name} />,
    );
  }

  return (
    <>
      <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
        <FlexItem grow={{ default: 'grow' }}>
          <FormSelect
            id={`${idPrefix}-category`}
            value={String(value)}
            onChange={(_event, selected) => onChange(Number(selected))}
            isDisabled={isDisabled || isSaving}
            validated={error ? 'error' : 'default'}
            aria-label="Category"
          >
            {options}
          </FormSelect>
        </FlexItem>
        <FlexItem>
          <Button
            variant="secondary"
            icon={<PlusIcon />}
            aria-label="Create a new category"
            onClick={() => setIsCreating(true)}
            isDisabled={isDisabled || isCreating}
          />
        </FlexItem>
      </Flex>

      {categoriesError && categories.length === 0 && (
        <FormHelperText>
          <HelperText>
            <HelperTextItem variant="warning">
              Couldn&apos;t load categories. You can still create one with the “+” button.
            </HelperTextItem>
          </HelperText>
        </FormHelperText>
      )}

      {error && (
        <FormHelperText>
          <HelperText>
            <HelperTextItem variant="error">{error}</HelperTextItem>
          </HelperText>
        </FormHelperText>
      )}

      {isCreating && (
        <div className="pf-v6-u-mt-sm">
          <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
            <FlexItem grow={{ default: 'grow' }}>
              <TextInput
                id={`${idPrefix}-new-category`}
                value={newName}
                maxLength={64}
                onChange={(_event, v) => {
                  setNewName(v);
                  if (createError) {
                    setCreateError(null);
                  }
                }}
                onKeyDown={(event) => {
                  // This input lives inside the product form — keep Enter/Escape from submitting or
                  // closing it; drive the inline create instead.
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleCreate();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelCreate();
                  }
                }}
                validated={createError ? 'error' : 'default'}
                isDisabled={isSaving}
                placeholder="New category name"
                aria-label="New category name"
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="primary"
                icon={<CheckIcon />}
                aria-label="Save new category"
                onClick={() => void handleCreate()}
                isLoading={isSaving}
                isDisabled={isSaving || newName.trim() === ''}
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="plain"
                icon={<TimesIcon />}
                aria-label="Cancel new category"
                onClick={cancelCreate}
                isDisabled={isSaving}
              />
            </FlexItem>
          </Flex>
          {createError && (
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant="error">{createError}</HelperTextItem>
              </HelperText>
            </FormHelperText>
          )}
        </div>
      )}
    </>
  );
}

export default CategorySelectField;
